import React, { useEffect, useState } from 'react';
import { CheckCircle2, Download, Loader2 } from 'lucide-react';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';
import { platformAPI } from '../../services/api';
import { saveBlob } from '../../services/download';

export const EUBSO_INDICATORS = [
  { id: 'energy_savings_pct', label: 'Energy savings (%)' },
  { id: 'co2_reduction_tons', label: 'CO₂ reduction (t)' },
  { id: 'dynamic_epc_score',  label: 'Dynamic EPC score' },
];

const POLL_MS = 3000;
const FINISHED = ['completed', 'failed'];
const isoDay = (date) => date.toISOString().slice(0, 10);
const defaultPeriod = () => ({
  from: isoDay(new Date(Date.now() - 90 * 24 * 3600 * 1000)),
  to: isoDay(new Date()),
});

const Fieldset = ({ legend, children }) => (
  <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5 min-w-0">
    <legend className="mb-1.5 p-0 text-[13px] font-semibold text-lb-ink">{legend}</legend>
    {children}
  </fieldset>
);

/**
 * EU BSO report: choose a period, the buildings shown or all of them, and
 * the indicators; generate the aggregation job, follow its status, then
 * download the JSON-LD. `canDownload` reflects eubso:export.
 *
 * Mount it only while it is open, so every opening starts a fresh report.
 */
export const EubsoReportDialog = ({ onClose, buildingIds = [], allBuildingsLabel = 'All buildings', canDownload }) => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [scope, setScope] = useState(() => (buildingIds.length > 0 ? 'shown' : 'all'));
  const [indicators, setIndicators] = useState(EUBSO_INDICATORS.map(({ id }) => id));
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const jobId = job?.id;
  const finished = !job || FINISHED.includes(job.status);
  useEffect(() => {
    if (!jobId || finished) return undefined;
    const timer = setInterval(async () => {
      try {
        const latest = await platformAPI.getEubsoJob(jobId);
        setJob({ id: jobId, status: latest.status });
      } catch (err) {
        setError(err.message || 'Could not check the report status.');
        setJob({ id: jobId, status: 'failed' });
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [jobId, finished]);

  const toggleIndicator = (id) =>
    setIndicators((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  const generate = async (event) => {
    event.preventDefault();
    if (indicators.length === 0) {
      setError('Choose at least one indicator.');
      return;
    }
    if (!period.from || !period.to || period.from > period.to) {
      setError('Choose a start date on or before the end date.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await platformAPI.createEubsoJob({
        timeRange: period,
        buildingSubset: scope === 'shown' ? buildingIds.map(String) : [],
        indicatorSet: EUBSO_INDICATORS.map(({ id }) => id).filter((id) => indicators.includes(id)),
      });
      setJob({ id: created.jobId, status: created.status || 'queued' });
    } catch (err) {
      setError(err.message || 'Could not start the report.');
    } finally {
      setSubmitting(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      saveBlob(`eubso-report-${job.id}.jsonld`, await platformAPI.downloadEubsoExport(job.id));
    } catch (err) {
      setError(err.message || 'Could not download the report.');
    } finally {
      setDownloading(false);
    }
  };

  const scopeText = scope === 'shown'
    ? `${buildingIds.length} ${buildingIds.length === 1 ? 'building' : 'buildings'}`
    : allBuildingsLabel.toLowerCase();

  let body;
  let footer;
  if (!job) {
    body = (
      <form id="eubso-report-form" onSubmit={generate} className="flex flex-col gap-5" noValidate>
        <Fieldset legend="Period">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date"
              aria-label="Start date"
              value={period.from}
              max={period.to || undefined}
              onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
            />
            <span className="text-[13px] text-lb-muted">to</span>
            <input
              type="date"
              aria-label="End date"
              value={period.to}
              min={period.from || undefined}
              onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
            />
          </div>
        </Fieldset>
        <Fieldset legend="Buildings">
          <label className="flex items-center gap-2 text-[13px] text-lb-ink">
            <input
              type="radio"
              name="eubso-scope"
              checked={scope === 'shown'}
              disabled={buildingIds.length === 0}
              onChange={() => setScope('shown')}
            />
            The {buildingIds.length} {buildingIds.length === 1 ? 'building' : 'buildings'} shown
          </label>
          <label className="flex items-center gap-2 text-[13px] text-lb-ink">
            <input type="radio" name="eubso-scope" checked={scope === 'all'} onChange={() => setScope('all')} />
            {allBuildingsLabel}
          </label>
        </Fieldset>
        <Fieldset legend="Indicators">
          {EUBSO_INDICATORS.map(({ id, label }) => (
            <label key={id} className="flex items-center gap-2 text-[13px] text-lb-ink">
              <input type="checkbox" checked={indicators.includes(id)} onChange={() => toggleIndicator(id)} />
              {label}
            </label>
          ))}
        </Fieldset>
      </form>
    );
    footer = (
      <div className="flex justify-end gap-2">
        <AppButton variant="ghost" onClick={onClose}>Cancel</AppButton>
        <AppButton type="submit" form="eubso-report-form" variant="primary" loading={submitting}>Generate report</AppButton>
      </div>
    );
  } else if (job.status === 'completed') {
    body = (
      <div className="flex items-start gap-3">
        <CheckCircle2 size={20} className="text-lb-green-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex flex-col gap-1 text-[13px] text-lb-ink">
          <p className="m-0 font-semibold">Report ready</p>
          <p className="m-0 text-lb-muted">
            {period.from} to {period.to} · {scopeText} · {indicators.length} {indicators.length === 1 ? 'indicator' : 'indicators'}
          </p>
          {!canDownload && (
            <p className="m-0 text-lb-muted">Downloading needs the EU BSO export permission. Ask your administrator if you need it.</p>
          )}
        </div>
      </div>
    );
    footer = (
      <div className="flex justify-end gap-2">
        <AppButton variant="ghost" onClick={onClose}>Close</AppButton>
        {canDownload && (
          <AppButton variant="primary" icon={<Download size={14} />} onClick={download} loading={downloading}>
            Download JSON-LD
          </AppButton>
        )}
      </div>
    );
  } else if (job.status === 'failed') {
    body = <p className="m-0 text-[13px] text-lb-ink">The report could not be generated.</p>;
    footer = (
      <div className="flex justify-end gap-2">
        <AppButton variant="ghost" onClick={onClose}>Close</AppButton>
        <AppButton variant="primary" onClick={() => { setJob(null); setError(null); }}>Try again</AppButton>
      </div>
    );
  } else {
    body = (
      <p className="m-0 flex items-center gap-2 text-[13px] text-lb-ink" role="status">
        <Loader2 size={16} className="animate-spin text-lb-muted" aria-hidden="true" />
        Generating the report for {scopeText}…
      </p>
    );
    footer = (
      <div className="flex justify-end">
        <AppButton variant="ghost" onClick={onClose}>Close</AppButton>
      </div>
    );
  }

  return (
    <AppModal
      open
      onClose={onClose}
      title="EU BSO report"
      description="Aggregate indicators for the EU Building Stock Observatory and download them as JSON-LD."
      maxWidth="560px"
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        {body}
        {error && <p role="alert" className="m-0 text-[13px] text-[#B85052]">{error}</p>}
      </div>
    </AppModal>
  );
};
